/**
 * browserCapabilityVocabulary — the one shared vocabulary for the browser-worker capability's
 * `blocked_reason`, the field that says WHY live browser work cannot run in this deployment.
 *
 * `blocked_reason` is what `routers.connections.browser_worker_capability()` publishes, and that
 * function derives it entirely from `openai_hosted_browser_adapter.hosted_browser_execution_usability()`.
 * The adapter's own comment declares the contract: *"Machine-readable blocked reasons. The operator
 * surface renders these; they are stable strings."* They are stable and machine-readable, and until
 * this module the operator surface rendered them as its human text — an English identifier inside a
 * Hebrew workspace, e.g. `קריאה חיה לא זמינה — hosted_browser_backend_disabled`.
 *
 * The reachable set is FIVE tokens, not the adapter's eight `BLOCKED_*` constants: the other three
 * (`origin_not_allow_listed`, `hosted_browser_transport_error`, `objective_is_not_read_only`) are
 * execution-time refusals raised during a session, so they can reach a session's own
 * `execution.blocked_reason` but never this capability. A capability map that carried them would be
 * claiming keys this producer cannot mint.
 *
 * Why this module exists at all, and why the map that preceded it could never match. The field used
 * to carry OpenClaw's own reuse-check fact — `no_browser_capable_adapter` — and task 134 re-homed
 * that fact to `connections.openclaw_browser_capability()`, where it still lives. `EmployeeWorkspace`
 * kept a one-entry map keyed on that old token, so after the re-home BOTH of its consumers were
 * unreachable by construction: `browser_worker_capability()` cannot return `no_browser_capable_adapter`
 * in any configuration. The map did not drift by omission; it was written for a different producer's
 * scale, which is why a key-set comparison against its two consumers would have passed while the
 * surface printed a token. The `/api/connections/health` `openclaw` block still carries that token
 * and is a *different* field with a different producer — this module words only the capability.
 *
 * Why an unrecognised value is labelled rather than echoed: `blocked_reason` has no prose writer at
 * all — every value `browser_worker_capability()` can return is an identifier from the adapter's own
 * constant block — so a value this map does not name carries nothing an operator can read, and
 * echoing it would put another English token in the Hebrew line this module exists to clean. That is
 * the treatment `esetProtectionVocabulary.protectionStatusMeta` gives an arm it does not name, and it
 * is the opposite of `errorReasonVocabulary`'s choice for `technical_trace.error`, which is a token
 * set WITH prose writers. The cost is stated rather than hidden: a producer rename reads as the
 * unrecognised wording instead of as the new token, so the parity pin in this module's test — which
 * reads the adapter's source for the set the capability can reach — is the only control that sees
 * the drift.
 *
 * The wire is unchanged by all of this: no token is added, removed, or reinterpreted, and both
 * producers keep writing exactly what they wrote before. This is a labelling change on read-only
 * surfaces.
 */

export const BROWSER_CAPABILITY_BLOCKED_HE = {
  // The production default (`OPENAI_HOSTED_BROWSER_MODE` unset or unrecognised).
  hosted_browser_backend_disabled: "מסלול הדפדפן החי כבוי בהתקנה זו",
  // Reachable in the DEV/lab mode: the simulated backend is configured, so no live session exists.
  live_browser_execution_not_configured: "לא הוגדר מסלול ביצוע חי — מצב מעבדה מדומה בלבד",
  no_allow_listed_origin: "לא הוגדר אף יעד מאושר (allow-list) לביצוע דפדפן",
  independent_security_review_not_recorded: "לא נרשמה סקירת אבטחה חיצונית נדרשת — הביצוע החי חסום",
  openai_credential_not_resolvable: "אישורי ההתחברות של ספק הדפדפן אינם ניתנים לפתרון",
};

/** A `blocked_reason` this build does not name. Named rather than echoed — see the header. */
export const BROWSER_CAPABILITY_BLOCKED_UNKNOWN = "סיבת החסימה לא זוהתה בגרסה זו";

/**
 * The Hebrew wording for a browser-worker capability `blocked_reason`. A missing value and a value
 * this build does not name both read as the unrecognised arm — never as the raw token.
 */
export function browserCapabilityBlockedLabel(value) {
  return BROWSER_CAPABILITY_BLOCKED_HE[value] || BROWSER_CAPABILITY_BLOCKED_UNKNOWN;
}
